import { STORAGE_KEYS } from '../Chat/constants/ui';

/**
 * Predefined demo/test user tokens (john, jane).
 */
export interface PredefinedToken {
  id: string;
  name: string;
  customerId: string;
  token: string;
}

export const PREDEFINED_TOKENS: PredefinedToken[] = [
  {
    id: 'john',
    name: 'John Doe',
    customerId: '1234567890',
    token:
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNzgyMjUwMzg4fQ.XQ0lo40irFnL0o5vTRUUz3Nk65W-raRwzemCt9t70Ys',
  },
  {
    id: 'jane',
    name: 'Jane Doe',
    customerId: '0987654321',
    token:
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIwOTg3NjU0MzIxIiwibmFtZSI6IkphbmUgRG9lIiwiaWF0IjoxNzgyMjUwMzg4fQ.6VUJMhCW1U6UhDZ7fBg8eIUxkF9fzjLWDJ8RAQCvOWE',
  },
];

export interface ResolvedAuth {
  token: string;
  customerId: string;
  customerName: string;
  isCustomToken: boolean;
}

/**
 * Resolves VITE_DEFAULT_TOKEN to applied auth state.
 * Supports predefined IDs (john, jane) or a raw JWT string.
 */
export function resolveDefaultAuth(defaultToken?: string): ResolvedAuth | null {
  if (!defaultToken?.trim()) {
    return null;
  }

  const trimmed = defaultToken.trim();
  const predefined = PREDEFINED_TOKENS.find((t) => t.id === trimmed);

  if (predefined) {
    return {
      token: predefined.token,
      customerId: predefined.customerId,
      customerName: predefined.name,
      isCustomToken: false,
    };
  }

  if (trimmed.includes('.')) {
    return {
      token: trimmed,
      customerId: '',
      customerName: '',
      isCustomToken: true,
    };
  }

  return null;
}

/**
 * Restores auth from localStorage when no env default token is configured.
 */
export function restoreStoredAuth(): ResolvedAuth | null {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) {
      return null;
    }

    const isPredefined = PREDEFINED_TOKENS.some((t) => t.token === token);

    return {
      token,
      customerId: localStorage.getItem(STORAGE_KEYS.CUSTOMER_ID) ?? '',
      customerName: localStorage.getItem(STORAGE_KEYS.CUSTOMER_NAME) ?? '',
      isCustomToken: !isPredefined,
    };
  } catch {
    return null;
  }
}

/**
 * Resolves initial auth: env default token first, then localStorage.
 */
export function resolveInitialAuth(defaultToken?: string): ResolvedAuth | null {
  return resolveDefaultAuth(defaultToken) ?? restoreStoredAuth();
}
